/**
 * EliminaCantiereModal — Scelta modalità eliminazione cantiere/manutenzione.
 *
 * Due opzioni + annulla:
 *   1) ARCHIVIA/NASCONDI (soft-delete via `DELETE /cantieri/{id}`)
 *      - cantiere rimosso dalle viste operative
 *      - tutti i dati economici e documentali restano nel DB
 *      - ripristinabile dal cestino
 *   2) ELIMINA DEFINITIVAMENTE (`DELETE /cantieri/{id}/purge`)
 *      - preview in-modal con conteggio record per collezione
 *      - richiede seconda conferma esplicita (checkbox + digitazione)
 *      - hard-delete cascade non reversibile
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Archive, Trash2, AlertTriangle, RefreshCw, ShieldAlert } from "lucide-react";
import api from "@/api";

const COLL_LABEL = {
  bolle: "Bolle",
  rapportini: "Rapportini",
  attivita: "Attività Gantt",
  cronoprogrammi: "Cronoprogrammi",
  richieste_materiali: "Richieste materiali",
  richieste_magazzino: "Richieste magazzino",
  richieste_fatturazione: "Richieste fatturazione",
  fatture: "Fatture",
  costi_variabili: "Costi variabili / rate",
  magazzino_movimenti: "Movimenti magazzino",
  documenti: "Documenti",
  fotografie: "Fotografie",
  comunicazioni: "Comunicazioni",
  eventi: "Eventi calendario",
  subappalti: "Subappalti",
  cedolini_mensili: "Cedolini mensili",
  saldi_aggiustamenti: "Saldi/aggiustamenti",
  notifiche_eventi_sent: "Notifiche inviate",
  spese_generali_storico: "Storico spese generali",
};

export default function EliminaCantiereModal({ cantiere, tipoLabel = "cantiere", onClose }) {
  const qc = useQueryClient();
  const [mode, setMode] = React.useState("choose"); // choose | confirm-hard
  const [ackChecked, setAckChecked] = React.useState(false);
  const [typedConfirm, setTypedConfirm] = React.useState("");

  const nome = cantiere?.nome || "";
  const id = cantiere?.cantiere_id;

  // Preview solo quando l'utente ha scelto "Elimina definitivamente"
  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ["purge-preview", id],
    queryFn: () => api.get(`/cantieri/${id}/purge-preview`).then((r) => r.data),
    enabled: mode === "confirm-hard",
  });

  const archive = useMutation({
    mutationFn: () => api.post(`/cantieri/${id}/archive`).then((r) => r.data),
    onSuccess: (d) => {
      if (d?.already_archived) {
        toast.info(`${tipoLabel} era già archiviato`);
      } else {
        toast.success(`${tipoLabel} archiviato — nascosto dalle viste operative, dati economici preservati`);
      }
      qc.invalidateQueries({ queryKey: ["cantieri"] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore archiviazione"),
  });

  const hardDelete = useMutation({
    mutationFn: () => api.delete(`/cantieri/${id}/purge`).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`${tipoLabel} eliminato definitivamente — ${d.totale} record collegati rimossi`);
      qc.invalidateQueries({ queryKey: ["cantieri"] });
      qc.invalidateQueries({ queryKey: ["programma-lavori-overview"] });
      qc.invalidateQueries({ queryKey: ["cashflow"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["contabilita"] });
      qc.invalidateQueries({ queryKey: ["iva"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore eliminazione"),
  });

  const canConfirmHard =
    ackChecked && typedConfirm.trim().toUpperCase() === "ELIMINA";

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-elimina-cantiere"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-xl rounded-md shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-slate-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-xl text-slate-900">
              Come vuoi eliminare questo {tipoLabel}?
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{nome}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="btn-close-elimina-modal"
            className="p-1.5 hover:bg-slate-100 rounded-sm"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {mode === "choose" && (
          <div className="p-5 space-y-3 overflow-y-auto">
            {/* Opzione 1: Archivia */}
            <button
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
              data-testid="btn-archivia"
              className="w-full text-left p-4 border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-md transition-colors flex gap-3 disabled:opacity-60"
            >
              <div className="w-10 h-10 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
                <Archive className="w-5 h-5 text-blue-700" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-slate-900 flex items-center gap-2">
                  Archivia / Nascondi
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-blue-100 text-blue-800 border border-blue-300 rounded-sm">
                    Consigliato
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Rimuove il {tipoLabel} dalle viste operative ma <b>conserva tutti i dati</b>{" "}
                  economici e documentali. Bolle, costi, fatture, IVA, cash flow, movimenti
                  contabili, richieste materiali, rapportini, cronoprogrammi, attività, allegati e
                  storico restano <b>attivi</b> e concorrono ai report storici (Contabilità, IVA,
                  Cash Flow, storico costi/fatture).{" "}
                  <span className="text-blue-700 font-bold">Reversibile.</span>
                </p>
              </div>
            </button>

            {/* Opzione 2: Elimina definitivamente */}
            <button
              onClick={() => setMode("confirm-hard")}
              data-testid="btn-elimina-definitiv"
              className="w-full text-left p-4 border-2 border-rose-200 hover:border-rose-500 hover:bg-rose-50 rounded-md transition-colors flex gap-3"
            >
              <div className="w-10 h-10 rounded-md bg-rose-100 flex items-center justify-center shrink-0">
                <ShieldAlert className="w-5 h-5 text-rose-700" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-rose-900">Elimina definitivamente</div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Elimina il {tipoLabel} e <b>tutti i dati collegati</b> da ogni area dell'app
                  (bolle, costi, fatture, IVA, cash flow, rapportini, richieste, allegati,
                  scadenze, notifiche future). Dashboard, Contabilità e Cash Flow si ricalcolano
                  automaticamente. <span className="text-rose-700 font-bold">NON reversibile.</span>
                </p>
              </div>
            </button>

            {/* Annulla */}
            <div className="pt-2 flex justify-end">
              <button
                onClick={onClose}
                data-testid="btn-annulla-elimina"
                className="px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50 rounded-sm"
              >
                Annulla
              </button>
            </div>
          </div>
        )}

        {mode === "confirm-hard" && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-5 bg-rose-50 border-b-2 border-rose-300 flex items-start gap-3">
              <AlertTriangle className="w-7 h-7 text-rose-700 shrink-0" />
              <div>
                <div className="font-display font-black text-rose-900 uppercase">Attenzione</div>
                <p className="text-sm text-rose-900 mt-0.5 leading-snug">
                  Questa operazione <b>eliminerà definitivamente</b> il {tipoLabel} e tutti i dati
                  collegati. <b>L'operazione non è reversibile.</b>
                </p>
              </div>
            </div>

            <div className="p-5 space-y-3">
              <div>
                <div className="overline text-slate-500">Record che verranno eliminati</div>
                {previewLoading || !preview ? (
                  <div className="mt-2 py-6 text-center text-slate-400 text-sm inline-flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Calcolo in corso…
                  </div>
                ) : preview.totale === 0 ? (
                  <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600">
                    Nessun record collegato: verrà eliminato solo il {tipoLabel} stesso.
                  </div>
                ) : (
                  <div className="mt-2 border border-slate-200 rounded-sm">
                    <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                      {Object.entries(preview.per_collection)
                        .sort((a, b) => b[1] - a[1])
                        .map(([coll, n]) => (
                          <li
                            key={coll}
                            className="p-2 flex justify-between items-center text-xs"
                            data-testid={`preview-${coll}`}
                          >
                            <span className="text-slate-700">{COLL_LABEL[coll] || coll}</span>
                            <span className="font-mono font-black text-rose-700">{n}</span>
                          </li>
                        ))}
                    </ul>
                    <div className="p-2 border-t border-slate-200 bg-slate-50 flex justify-between items-center text-sm">
                      <span className="font-bold uppercase text-slate-700">Totale record</span>
                      <span className="font-mono font-black text-rose-700" data-testid="preview-totale">
                        {preview.totale}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
                <b>Sicurezza:</b> non verranno toccati fornitori, clienti, lavoratori, utenti,
                magazzino materiali condivisi, configurazioni, costi fissi aziendali, audit log,
                timbrature e ferie.
              </div>

              <label className="flex items-start gap-2 text-xs text-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ackChecked}
                  onChange={(e) => setAckChecked(e.target.checked)}
                  data-testid="check-ack-hard"
                  className="mt-0.5"
                />
                <span>
                  Ho letto e comprendo: sto eliminando <b>definitivamente e in modo irreversibile</b>{" "}
                  <b>{nome}</b> e i suoi {preview?.totale ?? 0} record collegati.
                </span>
              </label>

              <div>
                <label className="text-[11px] uppercase font-bold text-slate-500 block mb-1">
                  Digita <span className="font-mono text-rose-700">ELIMINA</span> per confermare
                </label>
                <input
                  value={typedConfirm}
                  onChange={(e) => setTypedConfirm(e.target.value)}
                  data-testid="input-typed-confirm"
                  data-no-uppercase
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono uppercase tracking-widest"
                />
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
              <button
                onClick={() => {
                  setMode("choose");
                  setAckChecked(false);
                  setTypedConfirm("");
                }}
                data-testid="btn-back-choose"
                className="px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-white rounded-sm"
              >
                ← Indietro
              </button>
              <div className="flex-1" />
              <button
                onClick={onClose}
                data-testid="btn-annulla-hard"
                className="px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-white rounded-sm"
              >
                Annulla
              </button>
              <button
                onClick={() => hardDelete.mutate()}
                disabled={!canConfirmHard || hardDelete.isPending || previewLoading}
                data-testid="btn-conferma-hard"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-black text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
              >
                <Trash2 className="w-4 h-4" />
                {hardDelete.isPending ? "Elimino…" : "Elimina definitivamente"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
